<template>
  <div class="car-card is-info mt-3">
    <div class="field mt-2">
      <label class="label">{{ $t("CarReservationForm.SelectCarClass") }}</label>
      <div class="control">
        <div class="select">
          <select v-model="selectedCarClass">
            <option value="" disabled selected>
              {{ $t("CarReservationForm.SelectCarClassPlaceholder") }}
            </option>
            <option
              v-for="category in carCategories"
              :key="category.value"
              :value="category.value"
            >
              {{ category.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="field">
      <label class="label">{{
        $t("CarReservationForm.SelectCarTransmission")
      }}</label>
      <div class="control">
        <div class="select">
          <select v-model="selectedCarTransmissionType">
            <option value="" disabled selected>
              {{ $t("CarReservationForm.SelectCarTransmissionPlaceholder") }}
            </option>
            <option
              v-for="transmission in carTransmissionTypes"
              :key="transmission.value"
              :value="transmission.value"
            >
              {{ transmission.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="field" v-if="carReturnDateUnknown">
      <label class="label">{{ $t("CarReservationForm.CarReturnDate") }}</label>
      <Datepicker
        v-model="carReturnDateTime"
        :enableTimePicker="true"
        :format="formatDateTime"
        :minDate="minCarReturnDate ?? new Date()"
      ></Datepicker>
    </div>
    <div>
      <h5 class="title is-5">
        {{ $t("CarReservationForm.findDriver") }}
      </h5>
      <div class="field">
        <div
          v-for="driver in availableDrivers"
          :key="driver.id"
          class="control"
        >
          <label class="radio">
            <input
              type="radio"
              :value="driver"
              v-model="selectedDriver"
              @change="onDriverSelected"
            />
            {{ driver.firstName }} {{ driver.lastName }}
            <span v-if="driver.emailAddress" class="has-text-grey-light">
              ({{ driver.emailAddress }})
            </span>
          </label>
        </div>
        <div class="control">
          <label class="radio">
            <input
              type="radio"
              value="custom"
              v-model="selectedDriver"
              @change="onDriverSelected"
            />
            {{ $t("CarReservationForm.CustomDriver") }}
          </label>
        </div>

        <!-- Custom Driver Input Fields -->
        <div
          v-if="selectedDriver === 'custom'"
          class="mt-3 p-3"
          style="background-color: #f9f9f9; border-radius: 4px"
        >
          <div class="field">
            <label class="label">{{
              $t("CarReservationForm.CustomDriverFirstName")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="customDriverFirstName"
                @input="onCustomDriverChange"
                :placeholder="
                  $t('CarReservationForm.CustomDriverFirstNamePlaceholder')
                "
              />
            </div>
          </div>
          <div class="field">
            <label class="label">{{
              $t("CarReservationForm.CustomDriverLastName")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="customDriverLastName"
                @input="onCustomDriverChange"
                :placeholder="
                  $t('CarReservationForm.CustomDriverLastNamePlaceholder')
                "
              />
            </div>
          </div>
          <div class="field">
            <label class="label">{{
              $t("CarReservationForm.CustomDriverEmail")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="email"
                v-model="customDriverEmail"
                @input="onCustomDriverChange"
                :placeholder="
                  $t('CarReservationForm.CustomDriverEmailPlaceholder')
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getCarCategories,
  getCarTransmissionTypes,
  createDriverFromEmployee,
  createUnlistedDriver
} from "@/helpers/car.helper.js";

export default {
  name: "CarReservationModule",
  computed: {
    carCategories() {
      return getCarCategories(this.$t);
    },
    carTransmissionTypes() {
      return getCarTransmissionTypes(this.$t);
    }
  },
  data() {
    return {
      selectedCarClass: "",
      selectedCarTransmissionType: "",
      selectedDriver: null,
      customDriverFirstName: "",
      customDriverLastName: "",
      customDriverEmail: "",
      carReturnDateTime: null,
      carReturnDate: "",
      carReturnTime: "",
      Driver: {
        firstName: "",
        lastName: ""
      }
    };
  },
  props: {
    availableDrivers: {
      type: Array,
      required: true
    },
    carReturnDateUnknown: {
      type: Boolean,
      required: true
    },
    minCarReturnDate: {
      type: Date,
      required: true
    }
  },
  watch: {
    selectedCarClass() {
      this.emitCarDataChanged();
    },
    selectedCarTransmissionType() {
      this.emitCarDataChanged();
    },
    carReturnDateTime() {
      this.updateCarReturnDateAndTime();
      this.emitCarDataChanged();
    }
  },
  methods: {
    onDriverSelected() {
      if (this.selectedDriver === "custom") {
        // For custom driver, use the custom input fields
        this.Driver = {
          id: null,
          firstName: this.customDriverFirstName,
          lastName: this.customDriverLastName,
          emailAddress: this.customDriverEmail
        };
      } else if (this.selectedDriver) {
        // For selected driver from list, use helper function
        this.Driver = createDriverFromEmployee(this.selectedDriver);
      } else {
        // No driver selected
        this.Driver = {
          firstName: "",
          lastName: ""
        };
      }
      this.emitCarDataChanged();
    },
    onCustomDriverChange() {
      // Update Driver object when custom driver fields change
      if (this.selectedDriver === "custom") {
        this.Driver = {
          id: null,
          firstName: this.customDriverFirstName,
          lastName: this.customDriverLastName,
          emailAddress: this.customDriverEmail
        };
        this.emitCarDataChanged();
      }
    },
    updateCarReturnDateAndTime() {
      if (this.carReturnDateTime) {
        // Extract date part (YYYY-MM-DD format)
        this.carReturnDate = this.carReturnDateTime.toISOString().split("T")[0];

        // Extract time part (HH:MM format)
        const hours = this.carReturnDateTime
          .getHours()
          .toString()
          .padStart(2, "0");
        const minutes = this.carReturnDateTime
          .getMinutes()
          .toString()
          .padStart(2, "0");
        this.carReturnTime = `${hours}:${minutes}`;
      } else {
        this.carReturnDate = "";
        this.carReturnTime = "";
      }
    },
    formatDateTime(date) {
      if (!date) return "";
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      };
      return new Date(date).toLocaleString("nl-NL", options);
    },
    removeSelectedDriver() {
      this.selectedDriver = null;
      this.customDriverFirstName = "";
      this.customDriverLastName = "";
      this.customDriverEmail = "";
      this.Driver = {
        firstName: "",
        lastName: ""
      };
      this.emitCarDataChanged();
    },
    emitCarDataChanged() {
      this.$emit("carDataChanged", {
        selectedCarClass: this.selectedCarClass,
        selectedCarTransmissionType: this.selectedCarTransmissionType,
        driver: this.Driver,
        carReturnDate: this.carReturnDate,
        carReturnTime: this.carReturnTime
      });
    }
  }
};
</script>
<style scoped>
.car-card {
  border-left: 5px solid #3273dc;
  background-color: #eef5ff;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}

.driver-button {
  background-color: #f5f5f5;
}
</style>
